import { fn } from '@ember/helper';
import Divider from 'tio-ui/components/divider';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { eq, not } from 'tio-ui/utilities';
import { Section, VStack } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import Button from 'tio-ui/components/button';
import Checkbox from 'tio-ui/components/checkbox';
import Component from '@glimmer/component';
import ErrorMessages from 'tio-ui/components/error-messages';
import { PhCheckCircle, PhInfo, PhPencilSimple, PhWarning, PhXCircle } from 'ember-phosphor-icons';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TasProgramTemplateModel from 'tio-data/models/tas-program-template';
import type ConditionModel from 'tio-data/models/condition';

interface StepDefinition {
  id: string;
  label: string;
  isComplete: boolean;
  summaryText: string;
  summaryKind?: 'course-cash';
}

interface StepSubmitSignature {
  Args: {
    stepNumber: number;
    application: TasApplicationModel;
    programTemplate: TasProgramTemplateModel;
    conditionsForSubmit: readonly ConditionModel[];
    acceptedConditions: ConditionModel[];
    canSubmitApplication: boolean;
    isSubmitting: boolean;
    submitError: string | null;
    hasGpaIfRequired: boolean;
    showGPAField: boolean;
    completedSteps: StepDefinition[];
    currentStep: number;
    onDidUpdateAcceptedConditions: (conditions: ConditionModel[]) => void;
    isLpnSchoolSelected: boolean;
    onSubmit: () => void;
    onEditStep: (index: number) => void;
  };
}

export default class StepSubmit extends Component<StepSubmitSignature> {
  get displayNumber() {
    return this.args.stepNumber + 1;
  }

  get reviewableSteps() {
    return this.args.completedSteps.filter((_step, index) => index !== this.args.currentStep);
  }

  get incompleteSteps() {
    return this.reviewableSteps.filter((step) => !step.isComplete);
  }

  get allPreviousStepsComplete() {
    return this.incompleteSteps.length === 0;
  }

  toggleLpnDiscount = () => {
    this.args.application.lpnDiscountActivated = !this.args.application.lpnDiscountActivated;
  };

  <template>
    <Section>
      <:header>
        <div class="tio-row-tight">
          <span
            class="tio-inline-row-center w-7 h-7 rounded-full tio-bg-brand tio-fg-inverse tio-text-xs-bold"
          >{{this.displayNumber}}</span>
          <span>{{t "tas.benefits_summary.submit_application"}}</span>
        </div>
      </:header>
      <:body>
        <VStack @collapsed={{true}}>
          {{! Review summary of completed steps }}
          <div class="mb-4">
            <h3 class="font-semibold tio-fg-secondary mb-3">{{t
                "tas.guided.steps.review_title"
              }}</h3>
            {{#each this.reviewableSteps as |step index|}}
              <div class="tio-row py-2 border-b tio-border-subtle last:border-b-0">
                {{#if step.isComplete}}
                  <PhCheckCircle @size="20" @weight="fill" class="tio-fg-info shrink-0" />
                {{else}}
                  <span
                    class="tio-inline-row-center w-5 h-5 rounded-full bg-error-50 ring-2 ring-error-100 shrink-0"
                  >
                    <PhXCircle @size="20" @weight="fill" class="text-error-600" />
                  </span>
                {{/if}}
                <span
                  class="flex-1 text-sm
                    {{if step.isComplete 'tio-fg-secondary' 'font-medium text-error-700'}}"
                >{{step.label}}</span>
                {{#if step.summaryText}}
                  <span class="text-xs {{if step.isComplete 'tio-fg-muted' 'text-error-600'}}">
                    {{#if (eq step.summaryKind "course-cash")}}
                      <span aria-hidden="true">{{htmlSafe
                          (t "common.tas.course_cash.default")
                        }}</span>
                      <span class="sr-only">{{t "common.tas.course_cash.label"}}</span>
                    {{else}}
                      {{step.summaryText}}
                    {{/if}}
                  </span>
                {{/if}}
                <button
                  type="button"
                  class="tio-fg-brand hover:tio-fg-brand"
                  {{on "click" (fn @onEditStep index)}}
                >
                  <PhPencilSimple @size="16" />
                </button>
              </div>
            {{/each}}
          </div>

          <Divider class="my-4" />

          {{! LPN discount checkbox }}
          {{#if @isLpnSchoolSelected}}
            <div class="tio-row-top">
              <Checkbox
                @label={{t "lpn.discount_checkbox.label"}}
                @checked={{@application.lpnDiscountActivated}}
                @onChange={{this.toggleLpnDiscount}}
              />
              <button
                type="button"
                class="tio-inline-row-center tio-fg-disabled"
                title={{t "lpn.discount_checkbox.tooltip"}}
                aria-label={{t "lpn.discount_checkbox.tooltip"}}
              >
                <PhInfo @size="16" />
              </button>
            </div>
          {{/if}}

          {{! GPA warning }}
          {{#if @showGPAField}}
            {{#unless @hasGpaIfRequired}}
              <div
                class="tio-bg-warning-soft border tio-border-warning rounded-lg p-3 mb-4 tio-row-top-tight"
              >
                <PhWarning @size="20" class="tio-fg-warning shrink-0 mt-0.5" />
                <p class="text-sm tio-fg-warning">
                  {{t "tas.guided.gpa_required"}}
                </p>
              </div>
            {{/unless}}
          {{/if}}

          {{! Conditions }}
          {{#if @conditionsForSubmit.length}}
            <div class="mb-4">
              <h3 class="tio-text-sm-medium tio-fg-secondary mb-2">{{t
                  "tas.guided.steps.terms_and_conditions"
                }}</h3>
              <div class="text-sm">
                <AcceptConditions
                  @conditions={{@conditionsForSubmit}}
                  @onChange={{@onDidUpdateAcceptedConditions}}
                />
              </div>
            </div>
          {{/if}}

          {{! Submit }}
          <div class="py-4 tio-stack-center">
            <Button
              @isRunning={{@isSubmitting}}
              @isDisabled={{not @canSubmitApplication}}
              @intent="primary"
              data-test-submit-application
              {{on "click" @onSubmit}}
            >
              {{t "tas.benefits_summary.submit_application"}}
            </Button>
            <p class="tio-text-sm-light text-center text-pretty mt-2">
              {{t "tas.benefits_summary.helper_text"}}
            </p>
            {{#if @submitError}}
              <ErrorMessages @showErrors={{true}} @error={{@submitError}} class="text-sm mt-2" />
            {{/if}}
          </div>
        </VStack>
      </:body>
    </Section>
  </template>
}
